import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Package, Check } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ChatWidget from "@/components/site/ChatWidget";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import { PRODUCTS, PRODUCT_LIST } from "@/data/products";
import useLenis from "@/hooks/useLenis";

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 } }),
};

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setProp(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function ProductDetail() {
  const { slug } = useParams();
  const p = PRODUCTS[slug];
  useLenis();

  useEffect(() => {
    if (!p) return;
    document.title = `${p.name} — Aadiyogis EXIM Traders`;
    setMeta("description", p.seo);
    setProp("og:title", `${p.name} — Aadiyogis EXIM Traders`);
    setProp("og:description", p.seo);
    setProp("og:image", p.heroImg);
    setProp("og:type", "product");
    setCanonical(`${window.location.origin}/products/${p.slug}`);
    window.scrollTo(0, 0);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "product-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: p.name,
      description: p.seo,
      image: p.heroImg,
      brand: { "@type": "Brand", name: "Aadiyogis EXIM Traders" },
      category: "Agricultural Commodity",
    });
    document.getElementById("product-jsonld")?.remove();
    document.head.appendChild(ld);
    return () => document.getElementById("product-jsonld")?.remove();
  }, [p]);

  if (!p) return <Navigate to="/" replace />;

  const others = PRODUCT_LIST.filter((x) => x.slug !== slug);

  return (
    <div className="bg-[#F9F8F6]" data-testid={`product-detail-${slug}`}>
      <Header />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <motion.img
          src={p.heroImg} alt={p.name}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.15 }} animate={{ scale: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#0f2018]/95" />
        <div className="absolute inset-0 grain" />
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-36">
          <Link to="/#products" data-testid="product-back-link" className="inline-flex items-center gap-2 text-white/80 hover:text-[#E6C97A] text-sm mb-6 transition-colors">
            <ArrowLeft size={16} strokeWidth={1.5} /> All Products
          </Link>
          <p className="overline text-[#E6C97A]">Commodity</p>
          <h1 className="mt-3 font-serif-display text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            {p.name}
          </h1>
          <p className="mt-5 text-white/85 text-lg max-w-2xl">{p.tagline}</p>
        </div>
      </section>

      {/* Intro + meta */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div className="lg:col-span-7" variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
            <p className="overline">Overview</p>
            <p className="mt-5 text-[#1A3626] text-xl md:text-2xl font-serif-display leading-snug">{p.intro}</p>
          </motion.div>
          <motion.div className="lg:col-span-5 space-y-8" variants={fade} custom={1} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
            <div>
              <div className="overline text-[#1A3626]/60 flex items-center gap-2"><MapPin size={13} className="text-[#C5A059]" /> Primary Origin</div>
              <p className="mt-2 text-[#1A3626] text-lg">{p.origin}</p>
            </div>
            <div>
              <div className="overline text-[#1A3626]/60 flex items-center gap-2"><Package size={13} className="text-[#C5A059]" /> Packaging Options</div>
              <ul className="mt-3 space-y-2">
                {p.packaging.map((pk) => (
                  <li key={pk} className="flex items-start gap-2 text-[#374151]">
                    <Check size={16} className="text-[#C5A059] shrink-0 mt-0.5" strokeWidth={2} /> {pk}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Spec cards */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="product-specs">
          {p.items.map((it, i) => (
            <motion.div
              key={it.name}
              className="bg-white border border-[#1A3626]/10 p-7"
              variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            >
              <h3 className="font-serif-display text-2xl text-[#1A3626]">{it.name}</h3>
              <p className="mt-3 text-sm"><span className="overline text-[#C5A059]">Grades</span><br />{it.grades}</p>
              <p className="mt-3 text-sm text-[#4B5563]"><span className="overline text-[#1A3626]/50">Specifications</span><br />{it.specs}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="overline mb-6">From the origin</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {p.gallery.map((g, i) => (
            <motion.div key={g} className="relative overflow-hidden h-72 group" variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <img src={g} alt={`${p.name} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1A3626] relative overflow-hidden p-10 md:p-16 text-center">
          <div className="absolute inset-0 grain" />
          <div className="relative">
            <h2 className="font-serif-display text-white text-3xl md:text-5xl tracking-tight">
              Ready to source <span className="italic text-[#E6C97A]">{p.name.toLowerCase()}?</span>
            </h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">Tell us your grade, quantity and destination — our trade desk replies within one business day.</p>
            <Link to="/#rfq" data-testid="product-cta-rfq" className="mt-8 btn-gold inline-flex">
              Request a Quote <ArrowRight size={18} strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        {/* Other commodities */}
        <div className="mt-16">
          <p className="overline mb-6">Explore more</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/products/${o.slug}`} data-testid={`related-product-${o.slug}`} className="group relative overflow-hidden h-56 block">
                <img src={o.heroImg} alt={o.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="relative h-full flex items-end p-6">
                  <h3 className="font-serif-display text-white text-2xl">{o.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ChatWidget />
    </div>
  );
}
